/**
 * Markdown Content Renderer
 *
 * Renders markdown with GitHub-flavored markdown support and sanitization.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import './MarkdownContent.css';

interface MarkdownContentProps {
  /** Markdown content to render */
  content: string;
  /** Optional class name */
  className?: string;
  /** Whether to render inline (no block elements) */
  inline?: boolean;
  /** Max length before truncation (0 = no limit) */
  maxLength?: number;
}

/**
 * Pre-process markdown for security
 * - Sanitizes dangerous HTML
 * - Limits link protocols
 */
function sanitizeMarkdown(content: string): string {
  // Remove any raw HTML tags that could be dangerous
  // (ReactMarkdown handles most of this, but extra safety doesn't hurt)
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [], // Strip all HTML, keep only text
    ALLOWED_ATTR: [],
  });
}

/**
 * Custom link renderer that opens external links in new tab
 */
function LinkRenderer({ href, children }: { href?: string; children?: React.ReactNode }) {
  // Only allow safe protocols
  const safeProtocols = ['http:', 'https:', 'mailto:'];
  let isSafe = false;

  if (href) {
    try {
      const url = new URL(href, window.location.origin);
      isSafe = safeProtocols.includes(url.protocol);
    } catch {
      // Relative URL, allow it
      isSafe = true;
    }
  }

  if (!isSafe) {
    return <span className="unsafe-link">{children}</span>;
  }

  const isExternal = href?.startsWith('http://') || href?.startsWith('https://');

  return (
    <a
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  );
}

/**
 * Custom code block renderer
 */
function CodeRenderer({
  inline,
  className,
  children,
}: {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  if (inline) {
    return <code className="inline-code">{children}</code>;
  }

  return (
    <div className="code-block">
      {language && <div className="code-language">{language}</div>}
      <pre>
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
}

export function MarkdownContent({
  content,
  className = '',
  inline = false,
  maxLength = 0,
}: MarkdownContentProps) {
  // Truncate if needed
  let displayContent = content;
  let isTruncated = false;

  if (maxLength > 0 && content.length > maxLength) {
    displayContent = content.slice(0, maxLength);
    isTruncated = true;
  }

  // Sanitize the markdown (removes any HTML)
  const safeContent = sanitizeMarkdown(displayContent);

  // For inline mode, render without block elements
  if (inline) {
    return (
      <span className={`markdown-content inline ${className}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Convert block elements to inline
            p: ({ children }) => <span>{children}</span>,
            h1: ({ children }) => <strong>{children}</strong>,
            h2: ({ children }) => <strong>{children}</strong>,
            h3: ({ children }) => <strong>{children}</strong>,
            h4: ({ children }) => <strong>{children}</strong>,
            h5: ({ children }) => <strong>{children}</strong>,
            h6: ({ children }) => <strong>{children}</strong>,
            ul: ({ children }) => <span>{children}</span>,
            ol: ({ children }) => <span>{children}</span>,
            li: ({ children }) => <span>{children} </span>,
            blockquote: ({ children }) => <em>{children}</em>,
            a: LinkRenderer,
            code: CodeRenderer,
          }}
        >
          {safeContent}
        </ReactMarkdown>
        {isTruncated && <span className="truncated">...</span>}
      </span>
    );
  }

  // Full block mode
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: LinkRenderer,
          code: CodeRenderer,
        }}
      >
        {safeContent}
      </ReactMarkdown>
      {isTruncated && <p className="truncated">...</p>}
    </div>
  );
}
